import { SessionData, SlotData } from "../types/appTypes";
import { ModuleDataDB } from "../types/dataTypes";
import { ModifierRule, StateProperty, UserVisibilityRule, VisibilityRule } from "../types/decisionTreeServiceTypes";
import { addDurationToDate, parseISODuration } from "../utils/DateConversion";
import { _some } from "../utils/Iterator";

/**
 * Aligns a date to the most recent occurrence of a specific weekday (including today if applicable).
 * This ensures modules are immediately available when the user starts, aligned to the correct weekday.
 * @param date - The base date to align from
 * @param targetWeekday - The target weekday (0=Sunday, 1=Monday, ..., 6=Saturday)
 * @returns The aligned date (same day if already on target weekday, or previous occurrence)
 */
const alignToWeekday = (date: Date, targetWeekday: number): Date => {
    const currentWeekday = date.getDay();
    
    if (currentWeekday === targetWeekday) {
        // Already on the target weekday
        return date;
    }
    
    // Calculate days to subtract to get to previous occurrence
    const daysToSubtract = (currentWeekday - targetWeekday + 7) % 7;
    
    const alignedDate = new Date(date);
    alignedDate.setDate(alignedDate.getDate() - daysToSubtract);
    return alignedDate;
};

/**
 * Applies schedule configuration to adjust a module's start date.
 * Only applies when schedule config exists and is not a chained module.
 * @param baseStartDate - The calculated base start date
 * @param scheduleConfig - Optional schedule configuration
 * @returns The adjusted start date based on schedule, or the original date if no adjustment needed
 */
const applyScheduleAlignment = (baseStartDate: Date, scheduleConfig?: { type: string; alignToWeekday?: number }): Date => {
    if (!scheduleConfig) {
        return baseStartDate;
    }
    
    if (scheduleConfig.type === 'weekly-on-weekday' && scheduleConfig.alignToWeekday !== undefined) {
        return alignToWeekday(baseStartDate, scheduleConfig.alignToWeekday);
    }
    
    // Future schedule types can be added here
    // e.g., 'daily', 'custom', etc.
    
    return baseStartDate;
};

export const applyVisibilityRule = (rule: VisibilityRule, properties: StateProperty[] | string[]): boolean => {

    // required for typechecking of the includes function, see https://newbedev.com/argument-of-type-number-is-not-assignable-to-parameter-of-type-never-in-array-includes
    const props = properties as Array<StateProperty | string>;

    let shouldHide = false;
    if (rule.hideIf && rule.hideIf.length > 0) {
        shouldHide = !!rule.hideIf.find(cond => {
            if (Array.isArray(cond)) {
                return cond.every(c => props.includes(c));
            } else {
                return props.includes(cond);
            }
        });
    }
    if (shouldHide) {
        return false;
    }

    let shouldShow = true;
    if (rule.showIf && rule.showIf.length > 0) {
        shouldShow = !!rule.showIf.find(cond => {
            if (Array.isArray(cond)) {
                return cond.every(c => props.includes(c));
            } else {
                return props.includes(cond);
            }
        })
    }
    return shouldShow;
};

export const isVisibleForUserProperties = (rule: UserVisibilityRule, userProperties: string[]): boolean => {
    return applyVisibilityRule(rule, userProperties);
};

export const isVisibleForModuleModifiers = (rule: ModifierRule, modifiers: string[]): boolean => {
    return applyVisibilityRule(rule, modifiers);
};

export const sessionIsVisibleForUserWithModifiers = (userProperties: string[], session: SessionData, moduleModifiers: string[]): boolean => {
    return session.sections.some(sec => {
        const userVisibility = isVisibleForUserProperties(sec.generalDisplayRule, userProperties);
        if (sec.moduleDisplayRule) {
            return userVisibility && isVisibleForModuleModifiers(sec.moduleDisplayRule, moduleModifiers);
        } else {
            return userVisibility;
        }
    })
};

export const slotIsVisibleForUser = (userProperties: string[], slot: SlotData, moduleModifiers: string[]): boolean => {
    return Object.values(slot.sessions).some(session => sessionIsVisibleForUserWithModifiers(userProperties, session, moduleModifiers))
};

// all modifier rules pass for user (time-based visibility rules are not relevant)
export const moduleIsRelevantForUser = (userProperties: string[], module: ModuleDataDB, moduleModifiers: string[]): boolean => {
    return _some(module.slots, slot => slotIsVisibleForUser(userProperties, slot, moduleModifiers));
};

export const getResolvedModuleStartDate = (
    module: ModuleDataDB,
    modules: ModuleDataDB[],
    userStartDateISO: string | undefined
): Date | null => {
    if (!userStartDateISO) {
        return null;
    }

    const visibility = module.userSurveyStartBasedVisibility;
    if (!visibility?.duration) {
        return null;
    }

    const userStartDate = new Date(userStartDateISO);

    // Chained modules: start immediately after previous module ends
    // Schedule alignment is NOT applied to chained modules (they inherit alignment)
    if (visibility.previousModuleId) {
        const prevModule = modules.find((m) => m._id === visibility.previousModuleId);
        if (!prevModule?.userSurveyStartBasedVisibility?.duration) {
            return null;
        }

        const prevStart = getResolvedModuleStartDate(prevModule, modules, userStartDateISO);
        if (!prevStart) {
            return null;
        }

        const prevDuration = parseISODuration(prevModule.userSurveyStartBasedVisibility.duration);
        return addDurationToDate(prevStart, prevDuration);
    }

    // First module or standalone module: calculate from user start date
    // Apply schedule alignment if configured
    let baseStartDate: Date;
    
    if (visibility.relativeToUser) {
        const relative = parseISODuration(visibility.relativeToUser);
        baseStartDate = addDurationToDate(userStartDate, relative);
    } else {
        // No relative offset specified, start from user's start date
        baseStartDate = userStartDate;
    }

    // Apply schedule alignment (e.g., align to next Wednesday)
    return applyScheduleAlignment(baseStartDate, visibility.schedule);
}

function getResolvedModuleEndDate(
    module: ModuleDataDB,
    modules: ModuleDataDB[],
    userStartDateISO: string | undefined
): Date | null {
    const visibility = module.userSurveyStartBasedVisibility;
    if (!visibility?.duration) {
        return null;
    }

    const moduleStart = getResolvedModuleStartDate(module, modules, userStartDateISO);
    if (!moduleStart) {
        return null;
    }

    const duration = parseISODuration(visibility.duration);
    const moduleEnd = addDurationToDate(moduleStart, duration);
    return moduleEnd;
}


export const getPastApplicableModules = async (
    userProperties: string[],
    userStartDateISO: string | undefined,
    moduleModifiers: string[],
    allModules: ModuleDataDB[]
) => {
    const modulesApplicableToUser = allModules.filter(module => {
        return moduleIsRelevantForUser(userProperties, module, moduleModifiers)
    });
    const pastApplicableModules = modulesApplicableToUser.filter(module => {
        const moduleEnd = getResolvedModuleEndDate(module, allModules, userStartDateISO);
        if (moduleEnd) {
            const now = new Date();
            if (moduleEnd < now) {
                return true;
            }
        }
        return false;
    });
    return pastApplicableModules;
}

export const getModulesEndedInLast24Hours = async (
    userProperties: string[],
    userStartDateISO: string | undefined,
    moduleModifiers: string[],
    allModules: ModuleDataDB[]
) => {
    const modulesApplicableToUser = allModules.filter(module => {
        return moduleIsRelevantForUser(userProperties, module, moduleModifiers);
    });

    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

    const recentEndedModules = modulesApplicableToUser.filter(module => {
        const moduleEnd = getResolvedModuleEndDate(module, allModules, userStartDateISO);
        if (moduleEnd) {
            const now = new Date();
            if (moduleEnd < now && moduleEnd > twentyFourHoursAgo) {
                return true;
            }
        }
        return false;
    });

    return recentEndedModules;
};
